/**
 * ContextItemsSection component
 * Displays and manages a collection of context items for a chatbot session
 */
import { Plus, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SessionContextItem } from '@/types/chatbot'
import { ContextItemChip } from './ContextItemChip'
import { Button } from '@/components/ui/button'

export interface ContextItemsSectionProps {
  /** List of context items to display */
  items: SessionContextItem[]
  /** Callback when add button is clicked */
  onAdd?: () => void
  /** Callback when a context item's remove button is clicked */
  onRemove?: (item: SessionContextItem) => void
  /** Whether the section is in a loading state */
  isLoading?: boolean
  /** Whether the section is disabled (e.g., session ended) */
  disabled?: boolean
  /** Display in compact mode */
  compact?: boolean
  /** Additional CSS classes */
  className?: string
  /** Header title override */
  title?: string
  /** Show add button */
  showAddButton?: boolean
  /** Maximum items to show before collapsing */
  maxVisible?: number
}

export function ContextItemsSection({
  items,
  onAdd,
  onRemove,
  isLoading = false,
  disabled = false,
  compact = false,
  className,
  title = 'Context',
  showAddButton = true,
  maxVisible,
}: ContextItemsSectionProps) {
  const hasItems = items.length > 0
  const visibleItems = maxVisible ? items.slice(0, maxVisible) : items
  const hiddenCount = maxVisible ? Math.max(0, items.length - maxVisible) : 0

  return (
    <section
      className={cn('flex flex-col gap-2', className)}
      data-testid="context-items-section"
      aria-label={title}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-muted-foreground">{title}</h4>
        {showAddButton && onAdd && !disabled && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={onAdd}
            disabled={isLoading}
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </Button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : hasItems ? (
        <div className="flex flex-wrap gap-1.5">
          {visibleItems.map((item) => (
            <ContextItemChip
              key={`${item.type}-${item.value}`}
              item={item}
              onRemove={disabled ? undefined : onRemove}
              disabled={disabled}
              compact={compact}
            />
          ))}
          {hiddenCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs text-muted-foreground bg-muted/50">
              +{hiddenCount} more
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-4 text-center">
          <p className="text-sm text-muted-foreground">No context items</p>
          {showAddButton && onAdd && !disabled && (
            <p className="text-xs text-muted-foreground/70 mt-1">
              Add work items, wiki pages, or files to provide context
            </p>
          )}
        </div>
      )}
    </section>
  )
}
